'use client';

import React, { useState } from 'react';

/**
 * Portrait for a member card, with a graceful fallback.
 *
 * Most of the 549-member roster has no photograph: Parliament's published
 * roster carries names and seats only. Passing an empty src to <img> renders
 * the browser's broken-image icon and the alt text, which looked like a fault
 * on every card. This shows the member's initials instead - deliberately
 * plain, so it reads as "no photograph yet" rather than as a failure.
 *
 * onError covers the other case: a URL that exists but 404s or is blocked.
 */
interface MemberPortraitProps {
  src?: string | null;
  name: string;
  className?: string;
}

function initialsFor(name: string): string {
  const parts = (name || '')
    .replace(/^(Hon\.?|Dr\.?|Prof\.?|Rt\.?)\s+/i, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function MemberPortrait({ src, name, className = '' }: MemberPortraitProps) {
  const [failed, setFailed] = useState(false);
  const usable = src && src.trim() && !failed;

  if (!usable) {
    return (
      <div data-component-id="src/components/common/MemberPortrait.tsx:39:6" data-component-path="src/components/common/MemberPortrait.tsx" data-component-line="39" data-component-end-line="47" data-component-file="MemberPortrait.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`flex items-center justify-center bg-gray-200 ${className}`}
        role="img"
        aria-label={`${name} — no photograph available`}
      >
        <span data-component-id="src/components/common/MemberPortrait.tsx:44:8" data-component-path="src/components/common/MemberPortrait.tsx" data-component-line="44" data-component-end-line="46" data-component-file="MemberPortrait.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xl%20font-bold%20tracking-wide%20text-gray-500%20sm%3Atext-3xl%22%7D" className="text-2xl font-bold tracking-wide text-gray-500 sm:text-3xl">
          {initialsFor(name)}
        </span>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img data-component-id="src/components/common/MemberPortrait.tsx:53:4" data-component-path="src/components/common/MemberPortrait.tsx" data-component-line="53" data-component-end-line="59" data-component-file="MemberPortrait.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22alt%22%3A%22%5Bvar%3Aname%5D%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D"
      src={src as string}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className={className}
    />
  );
}
